/* eslint-disable prettier/prettier */
import React, { useState, useMemo } from 'react';
import styles from './DatePicker.module.css';

export interface DatePickerProps {
	/** Callback fired when the user clicks 'Select Date' */
	onSelect?: (startDate: Date | null, endDate: Date | null) => void;
	/** Callback fired when the user clicks 'Cancel' */
	onCancel?: () => void;
	/** Initial month to display */
	initialMonth?: Date;
	/** Pre-selected start date */
	initialStartDate?: Date | null;
	/** Pre-selected end date */
	initialEndDate?: Date | null;
	/** Dates to show a small dot indicator under */
	dottedDates?: Date[];
	/** Dates to highlight with a green text color */
	highlightedDates?: Date[];
}

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];
const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const DatePicker: React.FC<DatePickerProps> = ({
	onSelect,
	onCancel,
	initialMonth = new Date(),
	initialStartDate = null,
	initialEndDate = null,
	dottedDates = [],
	highlightedDates = [],
}) => {
	const [currentMonth, setCurrentMonth] = useState<Date>(
		new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1)
	);
	const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
	const [endDate, setEndDate] = useState<Date | null>(initialEndDate);

	// Helper: Check if two dates are the exact same day
	const isSameDate = (d1: Date | null, d2: Date | null) => {
		if (!d1 || !d2) return false;
		return (
			d1.getFullYear() === d2.getFullYear() &&
			d1.getMonth() === d2.getMonth() &&
			d1.getDate() === d2.getDate()
		);
	};

	// Helper: Check if a date is inside an array of dates
	const isDateInArray = (date: Date, array: Date[]) => {
		return array.some(d => isSameDate(d, date));
	};

	const handlePrevMonth = () => {
		setCurrentMonth(
			new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
		);
	};

	const handleNextMonth = () => {
		setCurrentMonth(
			new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
		);
	};

	const handleDayClick = (clickedDate: Date) => {
		if (!startDate || (startDate && endDate)) {
			// Start a new selection range
			setStartDate(clickedDate);
			setEndDate(null);
		} else if (clickedDate < startDate) {
			// Clicked before start date, reset start date
			setStartDate(clickedDate);
		} else {
			// Complete the range
			setEndDate(clickedDate);
		}
	};

	const handleClear = () => {
		setStartDate(null);
		setEndDate(null);

		// Optional: notify parent that selection has been cleared
		onSelect?.(null, null);
	};


	// Generate calendar grid (42 cells: 6 rows x 7 days)
	const calendarDays = useMemo(() => {
		const year = currentMonth.getFullYear();
		const month = currentMonth.getMonth();

		const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
		const firstDayOfWeek = new Date(year, month, 1).getDay();
		const daysInPrevMonth = new Date(year, month, 0).getDate();

		const days = [];

		// Previous month filler days
		for (let i = firstDayOfWeek - 1; i >= 0; i--) {
			days.push({
				date: new Date(year, month - 1, daysInPrevMonth - i),
				isCurrentMonth: false,
			});
		}

		// Current month days
		for (let i = 1; i <= daysInCurrentMonth; i++) {
			days.push({
				date: new Date(year, month, i),
				isCurrentMonth: true,
			});
		}

		// Next month filler days
		const remainingCells = 42 - days.length;
		for (let i = 1; i <= remainingCells; i++) {
			days.push({
				date: new Date(year, month + 1, i),
				isCurrentMonth: false,
			});
		}

		return days;
	}, [currentMonth]);

	return (
		<div className={styles.datePickerContainer}>
			{/* Header */}
			<div className={styles.header}>
				<span className={styles.monthYearLabel}>
					{MONTH_NAMES[currentMonth.getMonth()]}{' '}
					{currentMonth.getFullYear()}
				</span>
				<div className={styles.navButtons}>
					<button onClick={handlePrevMonth} className={styles.navBtn}>
						&lt;
					</button>
					<button onClick={handleNextMonth} className={styles.navBtn}>
						&gt;
					</button>
				</div>
			</div>

			{/* Weekdays */}
			<div className={styles.weekDays}>
				{WEEK_DAYS.map(day => (
					<div key={day} className={styles.weekDay}>
						{day}
					</div>
				))}
			</div>

			{/* Calendar Grid */}
			<div className={styles.daysGrid}>
				{calendarDays.map(({ date, isCurrentMonth }, index) => {
					const isSelectedStart = isSameDate(date, startDate);
					const isSelectedEnd = isSameDate(date, endDate);
					const isSelected = isSelectedStart || isSelectedEnd;

					const isBetween =
						startDate &&
						endDate &&
						date > startDate &&
						date < endDate;

					const isDotted = isDateInArray(date, dottedDates);
					const isHighlightedText = isDateInArray(
						date,
						highlightedDates
					);

					let cellClasses = styles.dayCell;
					if (!isCurrentMonth) cellClasses += ` ${styles.otherMonth}`;
					if (isBetween) cellClasses += ` ${styles.inRange}`;
					if (
						isSelectedStart &&
						endDate &&
						!isSameDate(startDate, endDate)
					)
						cellClasses += ` ${styles.rangeStart}`;
					if (
						isSelectedEnd &&
						startDate &&
						!isSameDate(startDate, endDate)
					)
						cellClasses += ` ${styles.rangeEnd}`;

					let numberClasses = styles.dayNumber;
					if (isSelected)
						numberClasses += ` ${styles.selectedNumber}`;
					else if (isHighlightedText && isCurrentMonth)
						numberClasses += ` ${styles.highlightedText}`;

					return (
						<div
							key={index}
							className={cellClasses}
							onClick={() => handleDayClick(date)}
						>
							<span className={numberClasses}>
								{date.getDate()}
								{isDotted && !isSelected && (
									<span className={styles.dotIndicator} />
								)}
							</span>
						</div>
					);
				})}
			</div>

			{/* Footer Actions */}
			<div className={styles.footer}>

				
					<button
						className={styles.primaryBtn}
						onClick={() => onSelect && onSelect(startDate, endDate)}
					>
						Select Date
					</button>

					<button
						className={styles.clearBtn}
						onClick={handleClear}
						disabled={!startDate && !endDate}
					>
						Clear
					</button>
					
					<button
						className={styles.secondaryBtn}
						onClick={() => {
							if (onCancel) onCancel();
							setStartDate(null);
							setEndDate(null);
						}}
					>
						Cancel
					</button>
				</div>
			</div>
			);
};
